/**
 * Which component renders each right-panel surface.
 *
 * This is the registry the port rules mean when CMMD features "integrate
 * through upstream's registries": a forge surface becomes a right-panel tab by
 * adding its kind to `RIGHT_PANEL_KINDS`, a definition to
 * `RIGHT_PANEL_KIND_DEFINITIONS`, an icon, and one entry here. The shell, the
 * tab strip, and the store need none of it, so no surface forks a panel
 * primitive of its own.
 *
 * The map is total over `RightPanelKind`, so a kind without a renderer is a
 * compile error rather than a tab that opens onto nothing. Each pane declares
 * the slice of `RightPanelPaneProps` it actually reads; the dispatch site
 * passes the whole bag, and a component accepting a subset is assignable to
 * one accepting the bag, which is what keeps a single call site honest without
 * handing every pane props it will never read.
 */
import { type ComponentType, Suspense, lazy } from "react";
import { type ScopedThreadRef } from "@cmmd-forge/contracts";
import { type TimestampFormat } from "@cmmd-forge/contracts/settings";

import type { RightPanelKind, RightPanelSurface } from "../rightPanelSurface";
import { BrowserPreviewPanel } from "./BrowserPreviewPanel";
import {
  DiffPanelHeaderSkeleton,
  DiffPanelLoadingState,
  DiffPanelShell,
  type DiffPanelMode,
} from "./DiffPanelShell";
import { PlanPanelContent } from "./PlanPanelContent";
import {
  ChatAgentsPane,
  ChatTaskPane,
  SimulatorPane,
  ThreadFilePane,
} from "./chat/ChatThreadAuxiliaryPanes";
import { FilesTreePane } from "./chat/FilesTreePane";
import { ChatTerminalPane } from "./chat/ChatTerminalPane";
import { ChatPullRequestPane } from "./pullRequest/ChatPullRequestPane";
import { DesktopPanel } from "./desktop/DesktopPanel";
import { DiffWorkerPoolProvider } from "./DiffWorkerPoolProvider";

const DiffPanel = lazy(() => import("./DiffPanel"));
const CanvasPanel = lazy(() => import("./CanvasPanel"));

/** Everything the dispatch site may pass. Each pane reads the slice it uses. */
export interface RightPanelPaneProps {
  readonly threadRef: ScopedThreadRef;
  /**
   * The active surface. Its kind is the key this pane was looked up by, so a
   * pane that only renders one kind can treat a mismatch as unreachable.
   */
  readonly surface: RightPanelSurface;
  readonly mode: DiffPanelMode;
  readonly shouldRenderDiffContent: boolean;
  readonly previewTabId: string | undefined;
  readonly timestampFormat: TimestampFormat;
  readonly closeRightPanel: () => void;
  readonly selectRightPanel: (kind: RightPanelKind) => void;
  readonly closeSurface: (surfaceId: string) => void;
}

/**
 * The browser preview panel, opened on the surface's preview tab.
 */
function PreviewPane(props: Pick<RightPanelPaneProps, "threadRef" | "previewTabId">) {
  return (
    <BrowserPreviewPanel
      environmentId={props.threadRef.environmentId}
      threadId={props.threadRef.threadId}
      {...(props.previewTabId ? { initialTabId: props.previewTabId } : {})}
    />
  );
}

/**
 * The Terminal tab's pane; closing it from inside closes the surface.
 */
function TerminalPane(props: Pick<RightPanelPaneProps, "threadRef" | "closeSurface">) {
  return (
    <ChatTerminalPane
      threadRef={props.threadRef}
      active
      onCloseSurface={() => props.closeSurface("terminal")}
    />
  );
}

/**
 * The canvas panel, loaded lazily behind a loading fallback.
 */
function CanvasPane() {
  return (
    <Suspense
      fallback={
        <div className="flex h-full items-center justify-center text-muted-foreground text-sm">
          Loading canvas...
        </div>
      }
    >
      <CanvasPanel />
    </Suspense>
  );
}

/**
 * The diff panel, mounted only once diff content may render.
 */
function DiffPane(props: Pick<RightPanelPaneProps, "mode" | "shouldRenderDiffContent">) {
  if (!props.shouldRenderDiffContent) return null;
  return (
    <DiffWorkerPoolProvider>
      <Suspense
        fallback={
          <DiffPanelShell mode={props.mode} header={<DiffPanelHeaderSkeleton />}>
            <DiffPanelLoadingState label="Loading diff viewer..." />
          </DiffPanelShell>
        }
      >
        <DiffPanel mode={props.mode} />
      </Suspense>
    </DiffWorkerPoolProvider>
  );
}

/**
 * The plan panel, with the thread's timestamp format.
 */
function PlanPane(
  props: Pick<RightPanelPaneProps, "threadRef" | "closeRightPanel" | "timestampFormat">,
) {
  return (
    <PlanPanelContent
      threadRef={props.threadRef}
      onClose={props.closeRightPanel}
      timestampFormat={props.timestampFormat}
    />
  );
}

/**
 * The thread's task pane.
 */
function TaskPane(props: Pick<RightPanelPaneProps, "threadRef">) {
  return <ChatTaskPane threadRef={props.threadRef} />;
}

/**
 * The simulator pane for the thread.
 */
function SimulatorPaneEntry(props: Pick<RightPanelPaneProps, "threadRef">) {
  return <SimulatorPane threadRef={props.threadRef} />;
}

/**
 * The thread's agents pane.
 */
function AgentsPane(props: Pick<RightPanelPaneProps, "threadRef">) {
  return <ChatAgentsPane threadRef={props.threadRef} />;
}

/**
 * The desktop display panel, pointing at the browser preview surface.
 */
function DisplayPane(props: Pick<RightPanelPaneProps, "threadRef" | "selectRightPanel">) {
  return (
    <DesktopPanel
      threadRef={props.threadRef}
      onOpenBrowser={() => props.selectRightPanel("preview")}
    />
  );
}

/**
 * The open-file pane for a file surface.
 */
function FilePane(props: Pick<RightPanelPaneProps, "surface" | "threadRef">) {
  // The registry dispatches by the active surface's kind; this guard only
  // teaches the type system what the key already guarantees.
  if (props.surface.kind !== "file") return null;
  return <ThreadFilePane surface={props.surface} threadRef={props.threadRef} />;
}

/**
 * The Files tree pane for the thread's workspace.
 */
function FilesPane(props: Pick<RightPanelPaneProps, "threadRef">) {
  return <FilesTreePane threadRef={props.threadRef} />;
}

/**
 * The pull request pane for a pull-request surface.
 */
function PullRequestPane(props: Pick<RightPanelPaneProps, "surface">) {
  if (props.surface.kind !== "pull-request") return null;
  return <ChatPullRequestPane surface={props.surface} />;
}

export const RIGHT_PANEL_PANES: Record<RightPanelKind, ComponentType<RightPanelPaneProps>> = {
  preview: PreviewPane,
  terminal: TerminalPane,
  canvas: CanvasPane,
  diff: DiffPane,
  plan: PlanPane,
  task: TaskPane,
  simulator: SimulatorPaneEntry,
  display: DisplayPane,
  agents: AgentsPane,
  file: FilePane,
  files: FilesPane,
  "pull-request": PullRequestPane,
};
